import { ToastrService } from 'ngx-toastr';
import { Component, OnInit, ViewChild } from '@angular/core';
import { CgpSnackbarService } from '../../../../../app/shared/cgp-snackbar/cgp-snackbar.service';
import { UserMaintenanceService, TprEcmUser } from 'src/modules/api/user-maintenance.service';
import { AddUserModalService } from '../user-maintenance-modal/add-user/add-user-modal.service';
import { FileDownloadService } from '../../../../shared/file-download/file-download.service';
import { UserService } from '../../../../../modules/user.service'
import { LazyLoadEvent } from 'primeng/api/primeng-api';
import { TprecmService } from '../../tprecm.service';
import { ColumnDef, PageOptions, DataTableValidator } from '../../../../shared/data-table/data-table.interface';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UserManageRequestAccessComponent } from './user-manage-request-access/user-manage-request-access.component';
import { Observable, BehaviorSubject } from 'rxjs';
import { DataTableService } from 'src/app/shared/data-table/data-table.service';

@Component({
   templateUrl: './user-maintenance-modal.template.html',
   styleUrls: ['../../tprecm.style.scss']
})
export class UserMaintenanceModalComponent implements OnInit {

   userRoles$ = new BehaviorSubject<any[]>([]);
   constSources$ = new BehaviorSubject<any[]>([]);
   designSources$ = new BehaviorSubject<any[]>([]);
   subscription$: Observable<any>;
   selectedUser: TprEcmUser;
   usersData;
   searchBarInput: any;
   lastNameFilter: any;

   pageOptions: PageOptions = this.dataTableService.pageOptionsDefaultSort('lastName');

   columns: ColumnDef[] = [
      { field: 'lastName', name: 'Last Name', editable: false },
      { field: 'firstName', name: 'First Name', editable: false },
      { field: 'initials', name: 'Initials', editable: false, width: '6em' },
      { field: 'gmin', name: 'GMIN', editable: false, width: '8em' },
      { field: 'gmid', name: 'gmid', canEditDisplay: false, visible: false },
      {
         field: 'userRoleId', name: 'Security Level', type: 'dropdown', optionsList: this.userRoles$, optionsListName: 'userRole', optionsListField: 'id',
         validators: this.securityLevelValidator((message: string) => this.toastr.error(message, 'Security and Permissions', { timeOut: 12000 }))
      },
      {
         field: 'userSourceId', name: 'Design Source', type: 'dropdown', optionsList: this.designSources$, optionsListName: 'sourceName', optionsListField: 'id', isEmptySelection: true,
         tooltip: 'Design Source Default for ECM'
      },
      {
         field: 'restrictedToId', name: 'Const Source Access', type: 'dropdown', optionsList: this.constSources$,
         optionsListName: 'sourceName', optionsListField: 'id', tooltip: 'Construction Source Restrictions',
         validators: this.constSourceValidator((message: string) => this.toastr.error(message, 'Security and Permissions', { timeOut: 12000 }))
      },
      { field: 'email', name: 'E-mail', width: '18em', editable: false },
      { field: 'active', name: 'Active?', type: 'checkbox', width: '6em', tooltip: 'Check box if worker is active.' }
   ];

   loading = false;
   total = 0;
   downloadUri = 'maint/user/download';

   private readonly _snackbarAction = 'Dismiss';

   securityLevelValidator(callback: (message: string) => void): DataTableValidator {
      return {
         validator: this.isInvalidRoleCheck,
         invalid: (column: ColumnDef) => callback(`All Super Users, Program MSEs, Regional MSEs, and EGMs must have access to ALL SOURCES.
         Please update the Const Source Restrictions field before updating the Security Level field. Data not saved.`),
         snippit: 'Invalid selections'
      };
   }

   constSourceValidator(callback: (message: string) => void): DataTableValidator {
      return {
         validator: this.isInvalidRoleCheck,
         invalid: (column: ColumnDef) => callback(`All Super Users, Program MSEs, Regional MSEs, and EGMs must have access to ALL SOURCES. Data not saved.`),
         snippit: 'Invalid selections'
      };
   }

   private isInvalidRoleCheck(field: any, data: TprEcmUser): boolean {
      // Roles greater than ID 4 do not require ALL SOURCES, if restrictedToId is 127 all roles are valid
      // Super User, Program MSE, Regional MSE ,EGM : 1,2,3,4
      // ALL SOURCES : 127
      return data.userRoleId > 4 || data.restrictedToId === 127;
   }

   constructor(
      private readonly dialog: MatDialog,
      private readonly cgpSnackbarService: CgpSnackbarService,
      private readonly userMaintenanceService: UserMaintenanceService,
      private readonly fileDownloadService: FileDownloadService,
      private readonly addUserModalService: AddUserModalService,
      private readonly tprEcmService: TprecmService,
      private readonly userService: UserService,
      private readonly toastr: ToastrService,
      private readonly dataTableService: DataTableService
   ) { }

   ngOnInit() {
      this.initUsers();
   }

   onSelect(user) {
      this.selectedUser = user;
   }

   onLazyLoad(event: LazyLoadEvent) {
      let sortName = event.sortField;
      const sortField = event.sortField.toLowerCase();
      // Hardcoding these field checks because
      // the service needs the specific name of the field
      // for sorting.
      if (sortField === 'restrictedtoid') {
         sortName = 'constSource';
      } else if (sortField === 'usersourceid') {
         sortName = 'userSourceName';
      } else if (sortField === 'userroleid') {
         sortName = 'userRole';
      }
      this.pageOptions.sort = sortName || this.pageOptions.sort;
      this.pageOptions.order = event.sortOrder === 1 ? 'asc' : 'desc';
      this.pageOptions.page = event.first / (event.rows || this.pageOptions.size);
      this.pageOptions.size = event.rows || this.pageOptions.size;
      this.loadUsers();
   }

   initUsers() {
      this.userMaintenanceService.getRoles()
         .subscribe(res => this.userRoles$.next(res));

      this.userMaintenanceService.getDesignSources()
         .subscribe(res => this.designSources$.next(res));

      this.userMaintenanceService.getConstSources()
         .subscribe(res => this.constSources$.next(res));
   }

   loadUsers() {
      this.loading = true;
      this.userMaintenanceService.getUsers(this.lastNameFilter, this.pageOptions)
         .subscribe({
            next: res => {
               if (res) {
                  this.usersData = res.data;
                  this.total = res.totalCount;
               }
            },
            complete: () => this.loading = false
         });
   }

   refreshUsers() {
      this.pageOptions.sort = 'lastName';
      this.pageOptions.order = 'asc';
      this.loadUsers();
   }

   onAddUserClick() {
      this.addUserModalService.showModal().afterClosed().subscribe(() => {
         this.pageOptions.sort = 'id';
         this.pageOptions.order = 'desc';
         this.selectedUser = null;
         this.loadUsers();
      });
   }

   onReportClick() {
      this.cgpSnackbarService.showSnackbar({
         message: 'Attachment downloading...',
         action: this._snackbarAction,
      });
      const date = new Date();
      const filename = `rptUsers_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.xlsx`;
      this.fileDownloadService.downloadFile(this.downloadUri, filename)
         .subscribe();
   }

   updateUser(dataItem) {
      const user = dataItem.data;
      this.userMaintenanceService.updateUser(user).subscribe();
      this.userService.login().subscribe();
   }

   emailUserHandler() {
      if (!this.selectedUser) {
         this.toastr.error('Please select a user before Emailing');
         return;
      }

      if (!this.selectedUser.active) {
         this.toastr.error('Cannot e-mail a user who is inactive');
         return;
      }

      this.userMaintenanceService.sendEmail(this.selectedUser.gmin)
         .subscribe(() => this.toastr.success('User e-mail successfully sent.'));
   }

   onManageRequestAccessClick() {
      this.dialog.open(UserManageRequestAccessComponent, {
         disableClose: true,
         width: '74vw',
         height: '74vh',
      })
         .afterClosed()
         .subscribe(() => {
            this.selectedUser = null;
            this.loadUsers();
         });
   }

   searchByUser(event: any) {
      this.lastNameFilter = event.value;
      this.refreshUsers();
   }

   clearSearch() {
      this.searchBarInput = '';
      this.lastNameFilter = '';
      this.refreshUsers();
   }

}
